/**
 * The scanner's report, over whatever page asked for it.
 *
 * The call list is the reason it exists. A lead row says a site is three major
 * versions behind; the next question is always "behind on what, exactly", and
 * until now the only answer was to leave the list, retype the domain on the
 * scanner page, and come back to a filter bar that had forgotten everything -
 * the leads page keeps its filters in component state, so navigating away
 * really does throw the week's work queue on the floor.
 *
 * So the report comes to the list instead. Same component the scanner page
 * draws (`ScanDetail`), same stored scan, one extra way out: the header carries
 * a link to the page itself, which is where the raw material and the rest of
 * the table live.
 *
 * The fetch is deliberately the cached one. Opening a row must not send the
 * platform out to crawl somebody's website - `refresh` is the button, not the
 * side effect of looking.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ExternalLink, Loader2, Radar } from "lucide-react";
import { scanSite, type SiteScan } from "../../../lib/siteScan";
import { Modal } from "../../ui";
import { scanReportPath } from "../prospecting/steps";
import { ScanDetail } from "./ScanDetail";
import { scanErrorKey } from "./useSiteScan";
import type { T } from "./types";

type State =
  | { kind: "busy"; scan: SiteScan | null }
  | { kind: "done"; scan: SiteScan }
  | { kind: "error"; messageKey: string };

export function ScanDetailDialog({ domain, onClose, t }: { domain: string; onClose: () => void; t: T }) {
  const [state, setState] = useState<State>({ kind: "busy", scan: null });

  const load = useCallback(
    async (refresh: boolean) => {
      // A rescan keeps the previous report on screen rather than blanking the
      // dialog: the numbers are about to change, and watching them change is
      // the point of pressing the button.
      setState((prev) => ({ kind: "busy", scan: prev.kind === "error" ? null : prev.scan }));
      try {
        setState({ kind: "done", scan: await scanSite(domain, refresh) });
      } catch (err) {
        setState({ kind: "error", messageKey: scanErrorKey(err) });
      }
    },
    [domain],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  const scan = state.kind === "error" ? null : state.scan;

  return (
    // Wider than the CRM's 520px default, and through `width` rather than
    // `max-width`, which `.crm-modal` would ignore. The report is two columns
    // of panels; at 520 they stack into a very long ribbon.
    <Modal
      title={
        <span className="flex items-center gap-2">
          <Radar className="size-4 text-primary" />
          {domain}
        </span>
      }
      label={t("scanner.title")}
      onClose={onClose}
      className="w-[min(1040px,100%)]"
      headExtra={
        <Link
          to={scanReportPath(domain)}
          className="crm-btn crm-btn-sm"
          onClick={onClose}
        >
          <ExternalLink className="size-3.5" />
          {t("scanner.action.openPage")}
        </Link>
      }
    >
      {/* No padding of its own: `.crm-modal` already carries 28px/30px, and a
          second layer here would push the panels into a narrower column than
          the page shows them in. */}
      <div className="pt-4">
        {state.kind === "error" ? (
          <p className="crm-alert">
            <AlertTriangle className="size-4 shrink-0" />
            {t(state.messageKey)}
          </p>
        ) : null}

        {state.kind === "busy" && !scan ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : null}

        {scan ? (
          <ScanDetail
            scan={scan}
            refreshing={state.kind === "busy"}
            onRefresh={() => void load(true)}
            showRaw={false}
            t={t}
          />
        ) : null}
      </div>
    </Modal>
  );
}
